import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import { Button } from '@rentspree/component-2023.components.atoms.button';

import { TenantScreeningFormState } from '../../types/tenantScreening';

export interface SuccessStepProps {
  formData: TenantScreeningFormState;
  onEmailSend: (emails: string[]) => void;
  onCopyLink: () => void;
  onGenerateQR: () => void;
  onSocialShare: (platform: string) => void;
  onGetMoreApplicants: () => void;
  onReturnToDashboard: () => void;
  onHomeClick?: () => void;
  isLoading?: boolean;
}

// Mock application link - will be generated from API
const MOCK_APPLICATION_LINK = 'https://apply.link/23xyz';

export const SuccessStep: React.FC<SuccessStepProps> = ({
  formData, // eslint-disable-line @typescript-eslint/no-unused-vars -- Will be used for displaying application summary in future implementation
  onEmailSend,
  onCopyLink,
  onGenerateQR,
  onSocialShare,
  onGetMoreApplicants,
  onReturnToDashboard,
  onHomeClick,
  isLoading = false,
}) => {
  const [emailInput, setEmailInput] = useState('');

  // Auto-generate/fetch application link when component mounts
  useEffect(() => {
    // In real implementation, this would call API to generate the link
    // For now, we use the mock link
    // Log formData when component mounts (will be removed in production)
  }, [formData]);

  const handleSendEmail = () => {
    if (emailInput.trim()) {
      const emails = emailInput
        .split(',')
        .map(email => email.trim())
        .filter(Boolean);
      onEmailSend(emails);
      setEmailInput('');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: '64px',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'black',
            borderRadius: 1,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={onHomeClick}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: 'white',
              borderRadius: '2px',
            }}
          />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 672,
            width: '100%',
            backgroundColor: 'background.paper',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1)',
          }}
        >
          {/* Success Content */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            {/* Success Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: '#34b844',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <CheckIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>

            {/* Success Text */}
            <Typography
              variant='h3'
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '24px', sm: '36px' },
                lineHeight: 1.1,
              }}
            >
              Success, your application link is ready
            </Typography>

            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                mb: 4,
                fontSize: '18px',
                lineHeight: 1.5,
                maxWidth: 577,
                mx: 'auto',
              }}
            >
              Send this link to applicants. When they apply, we'll run the
              reports you selected.
            </Typography>

            {/* Email Section */}
            <Box sx={{ mb: 4, textAlign: 'left' }}>
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '14px',
                }}
              >
                Send to applicant by email
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder='Enter one or more emails, separated by commas'
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  variant='outlined'
                  size='medium'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 46,
                    },
                  }}
                />
                <Button
                  variant='contained'
                  onClick={handleSendEmail}
                  disabled={!emailInput.trim() || isLoading}
                  sx={{
                    minWidth: 82,
                    height: 46,
                    backgroundColor: '#1f15e1',
                    '&:hover': {
                      backgroundColor: '#1a12c7',
                    },
                  }}
                >
                  Send
                </Button>
              </Box>
            </Box>

            {/* Link Sharing */}
            <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
              <TextField
                value={MOCK_APPLICATION_LINK}
                variant='outlined'
                size='medium'
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: 50,
                    backgroundColor: 'grey.50',
                  },
                }}
              />
              <IconButton
                onClick={onCopyLink}
                sx={{
                  width: 42,
                  height: 50,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                }}
              >
                <ContentCopyIcon fontSize='small' />
              </IconButton>
              <IconButton
                onClick={onGenerateQR}
                sx={{
                  width: 42,
                  height: 50,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                }}
              >
                <QrCodeIcon fontSize='small' />
              </IconButton>
              <IconButton
                onClick={() => onSocialShare('general')}
                sx={{
                  width: 42,
                  height: 50,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 1,
                }}
              >
                <ShareIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>

          {/* Bottom Section */}
          <Box
            sx={{
              backgroundColor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'grey.200',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: '20px',
              }}
            >
              Need more high-quality applicants?
            </Typography>

            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                mb: 3,
                fontSize: '16px',
                lineHeight: 1.5,
                maxWidth: 574,
                mx: 'auto',
              }}
            >
              Our listing service advertises your property on top rental sites
              to bring more qualified leads directly to you.
            </Typography>

            <Button
              variant='outlined'
              onClick={onGetMoreApplicants}
              disabled={isLoading}
              sx={{
                minWidth: 202,
                height: 52,
                borderColor: '#1976d2',
                color: '#1976d2',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#1565c0',
                },
              }}
            >
              Get more applicants
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: 1322,
            mx: 'auto',
          }}
        >
          <Button
            variant='outlined'
            onClick={onReturnToDashboard}
            disabled={isLoading}
            sx={{
              minWidth: 110,
              height: 42,
            }}
          >
            Back
          </Button>
          <Button
            variant='contained'
            onClick={onReturnToDashboard}
            disabled={isLoading}
            sx={{
              minWidth: 192,
              height: 40,
              backgroundColor: '#110000',
              '&:hover': {
                backgroundColor: '#000000',
              },
            }}
          >
            Return to dashboard
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SuccessStep;
